/**
 * Polish display copy for the 40+ emotion words.
 *
 * REVIEWED BY MARTA STANKIEWICZ — Drive doc 06, returned 2026-08-29.
 * This file was originally a development-side draft, because her first packet
 * (docs 01–05) never covered emotions.ts. She rewrote 137 of the 176 fields
 * rather than correcting them, so the Polish below is substantially hers: the
 * hints became fuller sentences, and the healing phrases moved from terse
 * statements to warmer, more invitational ones.
 *
 * Two normalisations were applied on import and are NOT her wording:
 *   - Six labels she rewrote came back capitalised while the other 38 were
 *     lowercase. Lowercased for consistency, since the picker renders them as
 *     chips side by side and Polish does not capitalise common nouns.
 *   - Markdown bold survived from Google Docs on guilt.integration and was
 *     stripped; these render as plain strings.
 *
 * ⚠ `word` IS AN IDENTIFIER, NOT DISPLAY TEXT
 * Emotion.word is persisted as sessions.emotion_label and read back later, e.g.
 * Journal does EMOTIONS.find(e => e.word === session.emotion_label) to recover
 * the healing phrase. Translating `word` in place would mean a Polish user's
 * session stores "żałoba", which then matches nothing on lookup — the healing
 * phrase would vanish, cross-session pattern intelligence would split by
 * language, and switching language would orphan every past session.
 *
 * So the English word stays the canonical id in the data and in the database.
 * This file supplies a separate `label` for display only, keyed BY the English
 * word. Components render `label`; storage keeps using `word`.
 *
 * GENDERED FORMS
 * Healing phrases are first person, which in Polish is where gender leaks in
 * through predicate adjectives ("jestem bezpieczna/bezpieczny"). Every phrase
 * below is built from verbs and nouns instead, so none of them assume a gender.
 * Integration actions are imperatives, which are naturally neutral.
 */
import type { Emotion } from './emotions'

export interface EmotionPl {
  /** Display word shown in the picker. The English `word` remains the stored id. */
  label: string
  hint: string
  healingPhrase: string
  integration: string
}

/** Keyed by the English Emotion.word — the stable identifier. */
export const EMOTIONS_PL: Record<string, EmotionPl> = {
  grief: {
    label: 'żal po stracie',
    hint: 'Ciężar, który powraca falami',
    healingPhrase: 'Otaczam miłością siebie i to, co przeżywam, oddając szacunek temu, co zostało utracone.',
    integration: 'Napisz jedno zdanie o tym, co opłakujesz. Zachowaj je dziś tylko dla siebie.',
  },
  sadness: {
    label: 'smutek',
    hint: 'Cichy ból i ciężar odczuwany za powiekami',
    healingPhrase: 'Pozwalam smutkowi łagodnie prowadzić mnie głębiej do mojego serca.',
    integration: 'Zrób dziś jedną małą rzecz, która przyniesie Ci ukojenie i troskę.',
  },
  anger: {
    label: 'złość',
    hint: 'Narastające gorąco i impuls, by się przeciwstawić.',
    healingPhrase: 'Szanuję swoją złość jako energię, która pokazuje mi, co jest dla mnie ważne i co potrzebuje ochrony.',
    integration: 'Napisz wszystko, co pozostało niewypowiedziane. Pozwól tym słowom pozostać dziś przy Tobie.',
  },
  shame: {
    label: 'wstyd',
    hint: 'Kurczenie się w sobie i pragnienie, by zniknąć.',
    healingPhrase: 'Nawet pośród wstydu moja pełnia i wartość pozostają we mnie.',
    integration: 'Podziel się dziś z zaufaną osobą jedną małą, prawdziwą rzeczą o sobie.',
  },
  fear: {
    label: 'lęk',
    hint: 'Oddech staje się krótszy, a ciało przygotowuje się do ochrony.',
    healingPhrase: 'Jestem przy sobie. Pozwalam sobie czuć i odnajduję oparcie w tej chwili.',
    integration: 'Nazwij konkretnie to, co budzi w Tobie lęk. Zapisz to, a następnie pozwól kartce przez chwilę to potrzymać.',
  },
  guilt: {
    label: 'poczucie winy',
    hint: 'Powracanie myślami do tego, co mogło wydarzyć się inaczej.',
    healingPhrase: 'Obdarzam wybaczeniem tę część siebie, która wtedy działała z poziomu dostępnej jej świadomości.',
    integration: 'Napisz, co dziś mogłoby wydarzyć się inaczej. Następnie zapisz wybór, który chcesz zabrać ze sobą w przyszłość.',
  },
  loneliness: {
    label: 'samotność',
    hint: 'Pragnienie kontaktu i bliskości, które pozostaje bez odpowiedzi.',
    healingPhrase: 'Wracam do siebie z obecnością i czułością, pamiętając, że więź ze sobą jest zawsze dostępna.',
    integration: 'Odezwij się dziś do jednej bliskiej osoby i daj jej znać, że pojawiła się dziś w Twoich myślach.',
  },
  hopelessness: {
    label: 'poczucie braku nadziei',
    hint: 'Szara mgła, w której trudno dostrzec dalszą drogę.',
    healingPhrase: 'Nawet kiedy droga pozostaje jeszcze niewidoczna, życie we mnie nadal porusza się krok po kroku.',
    integration: 'Znajdź jedną najmniejszą rzecz, która jest dziś w Twoim zasięgu. Pozwól, by właśnie ona była wystarczająca na tę chwilę.',
  },
  betrayal: {
    label: 'zdrada',
    hint: 'Coś, czemu zostało powierzone zaufanie, zostało naruszone.',
    healingPhrase: 'Wracam do zaufania do siebie, swojego wewnętrznego głosu i tego, co czuję.',
    integration: 'Zapisz jedną granicę, która pomoże Ci otoczyć siebie większą troską i szacunkiem.',
  },
  powerlessness: {
    label: 'bezsilność',
    hint: 'Poczucie utknięcia, pomniejszenia i trudności w wykonaniu kolejnego kroku.',
    healingPhrase: 'Moja sprawczość może zaczynać się od jednego małego wyboru. Wybieram krok, który jest dziś w moim zasięgu.',
    integration: 'Znajdź jedną rzecz, na którą masz dziś wpływ. Skieruj swoją energię właśnie tam.',
  },
  disappointment: {
    label: 'rozczarowanie',
    hint: 'Poczucie opadnięcia, gdy coś ważnego potoczyło się inaczej, niż było oczekiwane.',
    healingPhrase: 'Przyjmuję to, co się wydarzyło, i pozostawiam przestrzeń na nowe możliwości.',
    integration: 'Zapisz, czego pragnęło Twoje serce. Następnie zapisz, co nadal może się wydarzyć.',
  },
  resentment: {
    label: 'uraza',
    hint: 'Historia, która powraca, i napięcie chroniące serce.',
    healingPhrase: 'Pozwalam tej historii stopniowo tracić swoją moc nade mną i tworzę przestrzeń na coś nowego.',
    integration: 'Kiedy ta historia pojawi się ponownie, zauważ ją z łagodnością. Pozwól jej być przez chwilę bez podążania za nią.',
  },
  overwhelm: {
    label: 'przytłoczenie',
    hint: 'Za dużo naraz, za szybko, z poczuciem utraty oparcia.',
    healingPhrase: 'Zatrzymuję się. Oddycham. Wybieram jedną małą rzecz, którą mogę unieść.',
    integration: 'Zdejmij dziś jedną rzecz ze swojej listy. Pozwól sobie zostawić ją na później.',
  },
  anxiety: {
    label: 'niepokój',
    hint: 'Myśli przyspieszają, a oddech staje się płytszy.',
    healingPhrase: 'Wracam uwagą do tej chwili. Czuję oddech, ciało i miejsce, w którym jestem.',
    integration: 'Kiedy niepokój pojawi się ponownie, nazwij go łagodnie: „Czuję niepokój”. Następnie wykonaj jeden spokojny oddech.',
  },
  heartache: {
    label: 'ból serca',
    hint: 'Czuły, uporczywy ból odczuwany w okolicy serca.',
    healingPhrase: 'Otaczam swoje serce czułością i pozwalam mu otwierać się ponownie we własnym tempie.',
    integration: 'Daj sobie pięć minut, by pobyć z tym uczuciem. Oddychaj i pozwól mu być dokładnie takim, jakie jest.',
  },
  despair: {
    label: 'rozpacz',
    hint: 'Głęboka pustka, której trudno nadać słowa.',
    healingPhrase: 'Nawet pośród tej ciemności życie we mnie nadal trwa.',
    integration: 'Podziel się dziś z jedną zaufaną osobą tym, jak naprawdę się czujesz. Pozwól sobie powiedzieć tyle, ile jest dla Ciebie możliwe.',
  },
  helplessness: {
    label: 'bezradność',
    hint: 'Poczucie braku odpowiedzi i trudność w odnalezieniu tego, co zrobić dalej.',
    healingPhrase: 'Nawet bez wszystkich odpowiedzi mogę być dla siebie źródłem łagodności i troski.',
    integration: 'Wybierz dziś jeden prosty gest troski o swoje ciało — odpoczynek, ciepło, wodę, ruch albo spokojny oddech.',
  },
  bitterness: {
    label: 'gorycz',
    hint: 'Ciężar dawnych krzywd, który nadal pozostaje blisko serca.',
    healingPhrase: 'Pozwalam sobie stopniowo odkładać ciężar, który przestał mi służyć, i wybieram więcej przestrzeni dla siebie.',
    integration: 'Zapisz, ile energii pochłaniało noszenie tej historii. Następnie zapisz, co chciałoby otrzymać tę energię zamiast niej.',
  },
  jealousy: {
    label: 'zazdrość',
    hint: 'Ukłucie lęku związane z pragnieniem bliskości, wyboru i znaczenia.',
    healingPhrase: 'To, co widzę u innych, jest wskazówką, czego pragnę dla siebie.',
    integration: 'Zapisz, czego w tej chwili najbardziej potrzebujesz: bliskości, jasności, zapewnienia, uwagi, bezpieczeństwa czy czegoś innego.',
  },
  envy: {
    label: 'zawiść',
    hint: 'Porównywanie własnej drogi z cudzą i chwilowe oddalanie się od poczucia własnej wartości.',
    healingPhrase: 'To, co porusza mnie w życiu innych, może pokazywać mi coś, czego pragnę również dla siebie. Moja droga ma własny rytm i wartość.',
    integration: 'Zrób dziś kilka godzin przerwy od treści, które nasilają porównywanie. Skieruj uwagę na jedną rzecz, którą cenisz we własnym życiu.',
  },
  rejection: {
    label: 'odrzucenie',
    hint: 'Ścisk w klatce piersiowej i potrzeba wycofania się.',
    healingPhrase: 'Moja wartość pozostaje pełna, także wtedy, gdy ktoś wybiera inną drogę.',
    integration: 'Zrób dziś jedną rzecz, która przypomni Ci o Twojej wartości i połączeniu ze sobą.',
  },
  longing: {
    label: 'tęsknota',
    hint: 'Czułe sięganie sercem ku czemuś, co wydaje się daleko.',
    healingPhrase: 'Moja tęsknota pomaga mi usłyszeć, czego naprawdę pragnie moje serce.',
    integration: 'Zapisz wprost, za czym tęsknisz. Pozwól temu pragnieniu wybrzmieć takim, jakie jest.',
  },
  insecurity: {
    label: 'niepewność siebie',
    hint: 'Wewnętrzny krytyk staje się głośniejszy niż własny głos.',
    healingPhrase: 'Uczę się widzieć siebie wyraźniej, z większą życzliwością i prawdą.',
    integration: 'Zapisz trzy rzeczy z ostatniego tygodnia, które chcesz w sobie docenić.',
  },
  disconnection: {
    label: 'odcięcie',
    hint: 'Poczucie oddalenia od siebie, innych lub własnego ciała.',
    healingPhrase: 'Wracam uwagą do siebie i odnawiam kontakt poprzez oddech, ciało i obecność.',
    integration: 'Odłóż telefon na godzinę i pozwól sobie naprawdę być tam, gdzie jesteś.',
  },
  confusion: {
    label: 'zagubienie',
    hint: 'Wiele kierunków naraz i trudność w poczuciu, co jest właściwe.',
    healingPhrase: 'Kiedy zwalniam, tworzę przestrzeń, w której jasność może się pojawić.',
    integration: 'Zatrzymaj dziś zbieranie kolejnych informacji. Pobądź przez chwilę z tym, co już wiesz i czujesz.',
  },
  frustration: {
    label: 'frustracja',
    hint: 'Napięcie, zaciśnięcie i poczucie, że droga jest zablokowana.',
    healingPhrase: 'Pozwalam napięciu stopniowo mięknąć i otwieram przestrzeń na inne rozwiązanie.',
    integration: 'Zrób dwugodzinną przerwę od sprawy, która utknęła. Wróć do niej później z większą przestrzenią.',
  },
  numbness: {
    label: 'odrętwienie',
    hint: 'Cisza wewnątrz i trudność w poczuciu czegokolwiek.',
    healingPhrase: 'Szanuję ten moment zatrzymania. Moje ciało może wybierać ciszę, kiedy potrzebuje przestrzeni.',
    integration: 'Wyjdź na zewnątrz i przejdź się powoli przez kilka minut, pozwalając zmysłom zauważać otoczenie.',
  },
  tension: {
    label: 'napięcie',
    hint: 'Uniesione barki, zatrzymany oddech i ciało przygotowane do reakcji.',
    healingPhrase: 'Pozwalam ciału zmięknąć w tej chwili i odnaleźć trochę więcej przestrzeni.',
    integration: 'Opuść barki. Rozluźnij szczękę. Zauważ oddech. Powtórz to ponownie później w ciągu dnia.',
  },
  vulnerability: {
    label: 'wrażliwość',
    hint: 'Otwartość, szczerość i poczucie, że pokazujesz coś prawdziwego o sobie.',
    healingPhrase: 'Moja otwartość jest przestrzenią dla prawdy, bliskości i autentycznego kontaktu.',
    integration: 'Pozwól dziś zobaczyć jedną małą, prawdziwą część siebie w relacji, która daje Ci poczucie bezpieczeństwa.',
  },
  resignation: {
    label: 'rezygnacja',
    hint: 'Poczucie, że nadzieja przycichła, a energia do dalszego działania osłabła.',
    healingPhrase: 'Nawet mała iskra może przypomnieć mi, że coś we mnie nadal pozostaje żywe.',
    integration: 'Zapisz jedną rzecz, która została kiedyś odłożona, a nadal ma dla Ciebie znaczenie.',
  },
  conflicted: {
    label: 'rozdarcie',
    hint: 'Poczucie ciągnięcia w dwóch kierunkach i trudność w odnalezieniu jasności.',
    healingPhrase: 'Daję sobie czas i przestrzeń, aby pobyć z tym, co czuję, aż większa jasność zacznie się wyłaniać.',
    integration: 'Podaruj sobie 48 godzin przestrzeni przed podjęciem decyzji. Wróć do niej, kiedy pojawi się więcej jasności.',
  },
  'self-criticism': {
    label: 'samokrytyka',
    hint: 'Wewnętrzny głos, który skupia uwagę na tym, co mogło być lepsze.',
    healingPhrase: 'Widzę część siebie, która potrzebuje więcej czułości, i spotykam ją z cierpliwością oraz zrozumieniem.',
    integration: 'Za każdym razem, gdy odezwie się dziś wewnętrzny krytyk, odpowiedz sobie: „Słyszę ten głos. Wybieram mówić do siebie z większą czułością”.',
  },
  disgust: {
    label: 'obrzydzenie',
    hint: 'Silny impuls odsunięcia się od czegoś, co przekracza Twoje granice lub wartości.',
    healingPhrase: 'Słucham sygnałów swojego ciała i szanuję granice, które pokazują mi, co jest dla mnie właściwe.',
    integration: 'Nazwij wartość lub granicę, która została naruszona. Następnie zapisz, jak możesz otoczyć ją większą ochroną i szacunkiem.',
  },
  control: {
    label: 'potrzeba kontroli',
    hint: 'Silna potrzeba planowania, przewidywania i utrzymywania wszystkiego w określonych ramach.',
    healingPhrase: 'Rozluźniam potrzebę trzymania wszystkiego w swoich rękach i otwieram się na więcej zaufania do procesu.',
    integration: 'Pozwól dziś jednej małej rzeczy pozostać taką, jaka jest. Zauważ, co dzieje się wtedy w Twoim ciele.',
  },
  loss: {
    label: 'strata',
    hint: 'Kogoś lub czegoś ważnego już tutaj brakuje.',
    healingPhrase: 'Miłość, znaczenie i wspomnienia związane z tym, co utracone, pozostają częścią mojej historii.',
    integration: 'Zapisz jedno wspomnienie związane z tym, co zostało utracone. Pozwól mu mieć swoje miejsce w Twojej historii.',
  },
  emptiness: {
    label: 'pustka',
    hint: 'Cicha przestrzeń w miejscu, które kiedyś było czymś wypełnione.',
    healingPhrase: 'Pozwalam tej przestrzeni istnieć i z czasem otwierać się na to, co nowe.',
    integration: 'Pobądź dziś przez chwilę z ciszą. Oddychaj i zauważaj, co pojawia się w tej przestrzeni.',
  },
  hope: {
    label: 'nadzieja',
    hint: 'Delikatne uniesienie i poczucie, że światło zaczyna powracać.',
    healingPhrase: 'Otwieram się na możliwości, które mogą pojawić się przede mną, nawet zanim zobaczę całą drogę.',
    integration: 'Zapisz jedną rzecz, na którą naprawdę czekasz z nadzieją lub radością. Pozwól sobie przez chwilę ją poczuć.',
  },
  tenderness: {
    label: 'czułość',
    hint: 'Miękkość w sercu i poczucie bliskości z kimś, czymś lub sobą.',
    healingPhrase: 'Pozwalam sobie w pełni przyjąć tę czułość i miękkość.',
    integration: 'Pomyśl o osobie, na której Ci zależy, i powiedz jej dziś, co szczególnie w niej cenisz.',
  },
  gratitude: {
    label: 'wdzięczność',
    hint: 'Pełniejszy oddech i chwila, w której nawet zwyczajne rzeczy nabierają znaczenia.',
    healingPhrase: 'Czuję wdzięczność za wszystko, co wspierało mnie na drodze do tej chwili.',
    integration: 'Zapisz trzy rzeczy, za które czujesz dziś wdzięczność, szczególnie te małe i łatwe do przeoczenia.',
  },
  peace: {
    label: 'spokój',
    hint: 'Łagodna cisza wewnątrz i poczucie większej lekkości w ciele.',
    healingPhrase: 'Pozwalam spokojowi rozprzestrzeniać się łagodnie w tej chwili i w moim ciele.',
    integration: 'Podaruj sobie dziś godzinę, w której możesz pozostać bliżej tego spokoju. Wybierz to, co pomaga Ci go pielęgnować.',
  },
  joy: {
    label: 'radość',
    hint: 'Ciepło rozchodzące się po ciele i naturalna potrzeba uśmiechu.',
    healingPhrase: 'Pozwalam sobie w pełni przyjąć radość i zrobić dla niej miejsce w swoim życiu.',
    integration: 'Podziel się dziś tą radością z jedną osobą, która jest Ci bliska. Pozwól jej być częścią tej chwili.',
  },
  readiness: {
    label: 'gotowość',
    hint: 'Coś zaczyna się wewnętrznie układać i pojawia się poczucie, że można zrobić kolejny krok.',
    healingPhrase: 'Ufam temu, co dojrzewa i wyłania się we mnie.',
    integration: 'Wybierz jeden konkretny krok w stronę tego, na co pojawia się w Tobie gotowość.',
  },
  surrender: {
    label: 'odpuszczenie',
    hint: 'Rozluźnienie potrzeby kontrolowania i pozwolenie, by coś mogło rozwijać się własnym rytmem.',
    healingPhrase: 'Odpuszczam potrzebę poznania całej drogi i otwieram się na zaufanie temu, co stopniowo się wyłania.',
    integration: 'Zauważ jedną rzecz, którą próbujesz mocno popchnąć do przodu. Daj jej dziś trochę więcej przestrzeni i obserwuj, co się zmienia.',
  },
  wonder: {
    label: 'zachwyt',
    hint: 'Coś nieoczekiwanego otwiera Cię na nowe odczucie, znaczenie lub możliwość.',
    healingPhrase: 'Pozostawiam w sobie przestrzeń na to, co może mnie jeszcze zaskoczyć, poruszyć i otworzyć.',
    integration: 'Zapisz, co Cię zaskoczyło lub poruszyło. Pozwól znaczeniu tej chwili odsłaniać się stopniowo.',
  },
}

/** Polish intensity labels, keyed by the same 1–10 scale as INTENSITY_LABELS. */
export const INTENSITY_LABELS_PL: Record<number, string> = {
  1: 'ledwie obecne',
  2: 'słabe',
  3: 'łagodne',
  4: 'obecne',
  5: 'wyraźne',
  6: 'silne',
  7: 'intensywne',
  8: 'bardzo intensywne',
  9: 'przytłaczające',
  10: 'pochłaniające',
}

/**
 * An emotion with a display label attached. `word` is untouched and remains the
 * value written to sessions.emotion_label.
 */
export type LocalizedEmotion = Emotion & { label: string }

/**
 * Attaches display copy for the requested language.
 *
 * For English — or any language with no entry — `label` falls back to `word`,
 * so callers can render `label` unconditionally. `word` is never rewritten, in
 * any language, because it is the stored identifier.
 */
export function localizeEmotion(emotion: Emotion, lang?: string): LocalizedEmotion {
  if (lang !== 'pl') return { ...emotion, label: emotion.word }

  const pl = EMOTIONS_PL[emotion.word]
  if (!pl) return { ...emotion, label: emotion.word }

  return {
    ...emotion,
    label: pl.label,
    hint: pl.hint,
    healingPhrase: pl.healingPhrase,
    integration: pl.integration,
  }
}

/** Intensity label for the 1–10 slider, falling back to the English map. */
export function localizeIntensityLabel(
  level: number,
  english: Record<number, string>,
  lang?: string,
): string {
  if (lang !== 'pl') return english[level] ?? ''
  return INTENSITY_LABELS_PL[level] ?? english[level] ?? ''
}

/**
 * Localizes a stored `sessions.emotion_label` for display.
 *
 * The label is written by Arrival as the selected words joined with ', '
 * ("hopelessness, resentment"), or, for series sessions, as a free-text focus
 * title. Each comma-separated part that matches an emotion word is swapped for
 * its Polish label; anything else (a focus title, an unknown word) passes
 * through untouched. The stored value is never modified — this is read-side
 * only, and English returns the input unchanged.
 */
export function localizeEmotionLabel(label: string | null | undefined, lang?: string): string {
  if (!label) return ''
  if (lang !== 'pl') return label
  return label
    .split(', ')
    .map(part => {
      const word = part.trim()
      return EMOTIONS_PL[word]?.label ?? word
    })
    .join(', ')
}
